'use client';

import { useState, useRef, useEffect } from 'react';
import { uploadArtwork, pinArtwork, deleteArtwork } from '@/lib/actions';
import { Plus, Pin, Trash2, Image as ImageIcon, Check, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface Artwork {
  id: number;
  file_path: string;
  is_pinned: number;
}

interface Props {
  characterId: string;
  initialArtwork: any[];
}

export default function ArtworkGallery({ characterId, initialArtwork }: Props) {
  const router = useRouter();
  const [artwork, setArtwork] = useState<Artwork[]>(initialArtwork);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state when props change (after router.refresh())
  useEffect(() => {
    setArtwork(initialArtwork);
  }, [initialArtwork]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check size client-side (10MB)
    if (file.size > 10 * 1024 * 1024) {
       setError("Image is too big! (Max 10MB)");
       return;
    }

    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await uploadArtwork(characterId, formData);
      router.refresh(); 
    } catch (error: any) {
      console.error("Upload failed", error);
      setError("Failed to upload. The image might be too large or the server is busy.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePin = async (id: number) => {
    try {
      await pinArtwork(characterId, id);
      router.refresh();
    } catch (error) {
      console.error("Pin failed", error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to remove this image?")) return;
    try {
      await deleteArtwork(id);
      router.refresh();
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const pinned = artwork.find(a => a.is_pinned === 1) || artwork[0];

  return (
    <div className="space-y-6">
      {/* Primary Display */}
      <div className="group relative aspect-[3/4] bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl shadow-purple-950/20">
        {pinned ? (
          <img src={`/${pinned.file_path}`} alt="Pinned artwork" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-slate-700 bg-slate-900">
             <ImageIcon size={64} className="mb-4 opacity-20" />
             <p className="text-sm font-serif italic">No artwork yet</p>
          </div>
        )}
        
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-60" />
        
        {/* Upload Overlay */}
        <button 
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="absolute inset-0 flex items-center justify-center bg-purple-950/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed"
        >
          <div className="bg-purple-600 p-4 rounded-full shadow-lg shadow-purple-900/50 scale-90 group-hover:scale-100 transition-transform">
            {uploading ? (
              <Loader2 className="animate-spin h-6 w-6 text-white" />
            ) : (
              <Plus size={32} className="text-white" />
            )}
          </div>
        </button>
        <input 
          type="file" 
          ref={fileInputRef} 
          onChange={handleUpload} 
          className="hidden" 
          accept="image/*" 
        />
      </div>

      {error && (
        <p className="text-xs text-red-400 bg-red-950/30 p-2 rounded border border-red-900/50 italic">
          {error}
        </p>
      )}

      {/* Gallery Grid */}
      <div className="space-y-3">
        <h4 className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-2">
           <ImageIcon size={12} /> Collection ({artwork.length})
        </h4>
        <div className="grid grid-cols-3 gap-3">
          {artwork.map((item) => (
            <div key={item.id} className="group relative aspect-square bg-slate-900 border border-slate-800 rounded-lg overflow-hidden hover:border-purple-500/50 transition-all">
              <img src={`/${item.file_path}`} className="w-full h-full object-cover" />
              
              {/* Controls - Made more obvious */}
              <div className="absolute top-1 right-1 flex flex-col gap-1">
                 <button 
                   onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                   className="p-1.5 bg-slate-950/80 hover:bg-red-600 rounded-md text-white transition-colors border border-white/10"
                   title="Remove image"
                 >
                    <Trash2 size={12} />
                 </button>
                 {!item.is_pinned && (
                   <button 
                     onClick={(e) => { e.stopPropagation(); handlePin(item.id); }}
                     className="p-1.5 bg-slate-950/80 hover:bg-purple-600 rounded-md text-white transition-colors border border-white/10"
                     title="Pin as favorite"
                   >
                      <Pin size={12} />
                   </button>
                 )}
              </div>

              {item.is_pinned === 1 && (
                <div className="absolute top-1 left-1 bg-purple-600 text-white p-1 rounded-md shadow-lg border border-white/20">
                   <Check size={10} />
                </div>
              )}
            </div>
          ))}
          
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square border-2 border-dashed border-slate-800 rounded-lg flex flex-col items-center justify-center text-slate-700 hover:text-slate-500 hover:border-slate-700 transition-all bg-slate-900/50"
          >
             {uploading ? <Loader2 size={20} className="animate-spin" /> : <Plus size={24} />}
             <span className="text-[10px] mt-1 font-mono uppercase tracking-tighter">Add</span>
          </button>
        </div>
      </div>
    </div>
  );
}
